/**
 * Structured logging utility for frontend applications.
 *
 * Provides categorized, level-based logging to replace direct console.* calls.
 * All logs are routed through the unified console system for consistent filtering.
 *
 * @example
 * ```typescript
 * import logger from '@/utils/logger';
 *
 * // Basic usage
 * logger.info('API', 'Request sent');
 * logger.warn('UI', 'Large file detected:', fileSize);
 * logger.error('Auth', 'Login failed:', error);
 *
 * // Multiple arguments (preserves console.* formatting)
 * logger.info('API', 'Response:', data, 'Status:', status);
 * ```
 *
 * @see quickstart.md for detailed usage guide and migration examples
 */

import { unifiedConsole } from './unifiedConsole';

/**
 * Log level type union.
 *
 * Maps to console methods:
 * - 'debug', 'trace', 'info', 'log' → console.log
 * - 'warn' → console.warn
 * - 'error' → console.error
 */
export type LogLevel = 'debug' | 'trace' | 'info' | 'log' | 'warn' | 'error';

/**
 * Valid log levels for runtime validation.
 * @internal
 */
const VALID_LEVELS: readonly LogLevel[] = ['debug', 'trace', 'info', 'log', 'warn', 'error'] as const;

/**
 * Structured logging function with category prefixes and log levels.
 *
 * @param category - Category identifier for grouping related logs (e.g., 'API', 'UI', 'Auth').
 *                   Appears as "[Category]:" prefix in console output.
 * @param level - Log level controlling console method and visual styling.
 * @param args - Arguments to log. Passed through to console.* methods exactly as provided.
 *               Supports any type: strings, numbers, objects, arrays, etc.
 *
 * @remarks
 * - Invalid log levels trigger a console.warn and fall back to 'log' level
 * - Category prefix is prepended to output: `[Category]: <your message>`
 * - All arguments preserve native console.* formatting (objects, arrays, etc.)
 * - Use of `any[]` for args is justified to match console.* signature exactly
 *
 * @example
 * ```typescript
 * // Info message
 * logger.info('UI', 'Button clicked');
 *
 * // Warning with context
 * logger.warn('Upload', 'Large file detected:', fileName, fileSize);
 *
 * // Error with object
 * logger.error('API', 'Request failed:', error);
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function log(category: string, level: LogLevel, ...args: any[]): void {
  // Validate log level and fall back to 'log' if invalid
  let validatedLevel: LogLevel = level;

  if (!VALID_LEVELS.includes(level)) {
    // Use unifiedConsole for the warning as well
    unifiedConsole.output(
      'FE',
      'Logger',
      'warn',
      `Invalid log level "${level}". Valid levels: ${VALID_LEVELS.join(', ')}. Falling back to "log".`
    );
    validatedLevel = 'log';
  }

  // Route ALL logs through unified console (single point of output)
  // The unified console will handle filtering, prefixing, and actual console calls
  unifiedConsole.output('FE', category, validatedLevel, args[0], ...args.slice(1));
}

/**
 * Log a warning message with category prefix.
 *
 * Convenience method for `logger.log(category, 'warn', ...args)`.
 * Uses console.warn for visual distinction in browser DevTools.
 *
 * @param category - Category identifier (e.g., 'API', 'UI', 'Storage')
 * @param args - Arguments to log (any type, preserves console.warn formatting)
 *
 * @example
 * ```typescript
 * logger.warn('Upload', 'File size exceeds limit:', fileSize, 'MB');
 * logger.warn('API', 'Deprecated endpoint used:', endpoint);
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function warn(category: string, ...args: any[]): void {
  log(category, 'warn', ...args);
}

/**
 * Log an error message with category prefix.
 *
 * Convenience method for `logger.log(category, 'error', ...args)`.
 * Uses console.error for visual distinction in browser DevTools.
 *
 * @param category - Category identifier (e.g., 'API', 'Auth', 'Database')
 * @param args - Arguments to log (any type, preserves console.error formatting)
 *
 * @example
 * ```typescript
 * logger.error('Auth', 'Login failed:', error);
 * logger.error('API', 'Request failed with status:', response.status, response.data);
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function error(category: string, ...args: any[]): void {
  log(category, 'error', ...args);
}

/**
 * Log a debug message with category prefix.
 *
 * Convenience method for `logger.log(category, 'debug', ...args)`.
 * Uses console.log. Intended for verbose development logging.
 *
 * @param category - Category identifier (e.g., 'Render', 'State', 'Hook')
 * @param args - Arguments to log (any type, preserves console.log formatting)
 *
 * @example
 * ```typescript
 * logger.debug('Render', 'Component mounted with props:', props);
 * logger.debug('State', 'Value changed from', oldValue, 'to', newValue);
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function debug(category: string, ...args: any[]): void {
  log(category, 'debug', ...args);
}

/**
 * Log an informational message with category prefix.
 *
 * Convenience method for `logger.log(category, 'info', ...args)`.
 * Uses console.log. The default choice for general logging.
 *
 * @param category - Category identifier (e.g., 'Navigation', 'User', 'Config')
 * @param args - Arguments to log (any type, preserves console.log formatting)
 *
 * @example
 * ```typescript
 * logger.info('Navigation', 'Navigating to:', url);
 * logger.info('User', 'User logged in:', username);
 * logger.info('Config', 'API URL configured as:', apiUrl);
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function info(category: string, ...args: any[]): void {
  log(category, 'info', ...args);
}

/**
 * Default export: Logger object with log method.
 *
 * @example
 * ```typescript
 * import logger from '@/utils/logger';
 * logger.info('Category', 'message');
 * ```
 */
export default {
  log,
  warn,
  error,
  debug,
  info,
};
